import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

import { apiFetch } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import Header from "../../components/Header/Header";
import { Modal, Confirm } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip } from "../../components/UI/Feedback";

const properties = require("../../properties.json");

const EMPTY = { name: "", description: "", price: "", currency: "EGP", intervalMonths: 1, trialDays: 0, enabled: true };

/** Membership plans of a coach: the price, the length (1, 3 or 12 months) and the free trial days. */
export default function Plans() {
  const toast = useRef(null);
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [doomed, setDoomed] = useState(null);

  const load = () =>
    apiFetch("/plans")
      .then(setRows)
      .catch(() => setRows([]));

  useEffect(() => {
    document.title = properties.site_name + " | " + t("gym.plans");
    load();
  }, []);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });

  const save = () => {
    if (!form.name.trim()) {
      say("error", t("gym.plans.nameRequired"));
      return;
    }
    const body = {
      name: form.name,
      description: form.description,
      price: form.price === "" ? 0 : Number(form.price),
      currency: form.currency,
      intervalMonths: form.intervalMonths,
      trialDays: Number(form.trialDays) || 0,
      enabled: form.enabled,
    };
    setSaving(true);
    apiFetch(form.id ? `/plans/${form.id}` : "/plans", { method: form.id ? "PUT" : "POST", body })
      .then(() => {
        setForm(null);
        say("success", t("gym.plans.saved"));
        return load();
      })
      .catch((e) => say("error", (e && e.message) || t("common.failed")))
      .finally(() => setSaving(false));
  };

  const remove = (plan) =>
    apiFetch(`/plans/${plan.id}`, { method: "DELETE" })
      .then(() => {
        setDoomed(null);
        return load();
      })
      .catch((e) => {
        setDoomed(null);
        say("error", (e && e.message) || t("common.failed"));
      });

  const intervals = [
    { label: t("gym.plans.monthly"), value: 1 },
    { label: t("gym.plans.quarterly"), value: 3 },
    { label: t("gym.plans.yearly"), value: 12 },
  ];
  const intervalLabel = (m) => (intervals.find((i) => i.value === m) || {}).label || m;
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <>
      <Header
        pageName={t("gym.plans")}
        action={{ label: t("gym.plans.new"), icon: "pi pi-plus", onClick: () => setForm({ ...EMPTY }) }}
      />
      <Toast ref={toast} />
      <Confirm
        visible={!!doomed}
        onHide={() => setDoomed(null)}
        name={doomed ? doomed.name : ""}
        note={t("gym.plans.deleteNote")}
        onConfirm={() => remove(doomed)}
      />
      <Modal
        visible={!!form}
        onHide={() => setForm(null)}
        title={form && form.id ? t("gym.plans.edit") : t("gym.plans.new")}
        footer={
          <button type="button" className="ui-btn" disabled={saving} onClick={save}>
            {saving ? <i className="pi pi-spin pi-spinner"></i> : <i className="pi pi-check"></i>} {t("common.save")}
          </button>
        }
      >
        {form ? (
          <div className="ui-modal-form">
            <label className="ui-filter">
              <span>{t("gym.plans.name")}</span>
              <InputText value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.plans.description")}</span>
              <InputTextarea rows={3} value={form.description || ""} onChange={(e) => set({ description: e.target.value })} maxLength={600} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.plans.price")}</span>
              <InputText type="number" min="0" step="0.01" value={form.price} onChange={(e) => set({ price: e.target.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.plans.currency")}</span>
              <InputText value={form.currency} maxLength={3} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.plans.interval")}</span>
              <Dropdown options={intervals} value={form.intervalMonths} onChange={(e) => set({ intervalMonths: e.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.plans.trialDays")}</span>
              <InputText type="number" min="0" max="90" value={form.trialDays} onChange={(e) => set({ trialDays: e.target.value })} />
            </label>
            <label className="ui-filter is-inline">
              <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
              <span>{t("gym.plans.enabled")}</span>
            </label>
          </div>
        ) : null}
      </Modal>

      <div className="content">
        <div className="container">
          <div className="card">
            <Card>
              <DataTable
                value={rows || []}
                className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                emptyMessage={
                  rows === null ? (
                    <TableWait />
                  ) : (
                    <EmptyState
                      icon="pi pi-ticket"
                      title={t("gym.plans.emptyTitle")}
                      hint={t("gym.plans.emptyText")}
                      action={{ label: t("gym.plans.new"), icon: "pi pi-plus", onClick: () => setForm({ ...EMPTY }) }}
                    />
                  )
                }
              >
                <Column field="name" header={t("gym.plans.name")} />
                <Column header={t("gym.plans.price")} body={(r) => `${r.price} ${r.currency}`} />
                <Column header={t("gym.plans.interval")} body={(r) => intervalLabel(r.intervalMonths)} />
                <Column header={t("gym.plans.trialDays")} body={(r) => (r.trialDays > 0 ? r.trialDays : "—")} />
                <Column header={t("gym.plans.members")} field="activeMembers" />
                <Column header={t("gym.plans.status")} body={(r) => <Chip kind={r.enabled ? "ok" : "off"}>{r.enabled ? t("gym.plans.on") : t("gym.plans.off")}</Chip>} />
                <Column
                  header=""
                  body={(r) => (
                    <div className="p-d-flex">
                      <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => setForm({ ...r, price: String(r.price) })} />
                      <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => setDoomed(r)} />
                    </div>
                  )}
                />
              </DataTable>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
